import { useEffect, useRef, type ReactNode } from "react";
import { ArrowRight, CalendarDays, FileCheck2, History as HistoryIcon, Settings, SlidersHorizontal, Sparkles, Target, TrendingUp } from "lucide-react";
import { Link, useLocation, useSearch } from "wouter";
import { useMicroappContext } from "@/framework/microapp-context";

const nav = [
  ["/fiscal-year", "Fiscal Year", CalendarDays],
  ["/variance", "Variance", SlidersHorizontal],
  ["/explain", "Explain", Sparkles],
  ["/forecast", "Forecast", TrendingUp],
  ["/scenarios", "Scenarios", Target],
  ["/review", "Outlook Review", FileCheck2],
  ["/outlook-history", "Outlook History", HistoryIcon],
] as const;

export function Tag({ children, tone = "" }: { children: ReactNode; tone?: string }) {
  return <span className={`tag ${tone}`}>{children}</span>;
}

export function PageHead({ eyebrow, title, sub, action }: { eyebrow: string; title: string; sub: string; action?: string }) {
  return <div className="page-head"><div><div className="eyebrow">{eyebrow}</div><h1 data-testid="text-page-title">{title}</h1><p className="sub">{sub}</p></div>{action && <button className="btn" data-testid={`button-${action.toLowerCase().replaceAll(" ", "-")}`}>{action}<ArrowRight size={13} /></button>}</div>;
}

export function Shell({ children, active, certificationLabel = "Last certified 31 Jul 2026 · 14:28", onExplain }: { children: ReactNode; active: string; certificationLabel?: string; onExplain?: () => void }) {
  const { embedded } = useMicroappContext();
  const [location] = useLocation();
  const search = useSearch();
  const params = new URLSearchParams(search);
  const fiscalYear = Number(params.get("fy")) || 2026;
  const companyId = params.get("companyId");
  const workflowParams = new URLSearchParams(params);
  workflowParams.set("fy", String(fiscalYear));
  if (companyId) workflowParams.set("companyId", companyId);
  const workflowSearch = workflowParams.toString();
  const currentPath = location.split("?")[0];
  const activeIndex = nav.findIndex(
    ([href, label]) => currentPath === href || active === label,
  );
  const workflowNavRef = useRef<HTMLElement | null>(null);
  const activeStepRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const container = workflowNavRef.current;
    const activeStep = activeStepRef.current;
    if (!container || !activeStep) return;
    const left =
      activeStep.offsetLeft -
      (container.clientWidth - activeStep.offsetWidth) / 2;
    container.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
  }, [active, currentPath]);

  if (embedded) return <main className="workspace embedded-workspace">{children}</main>;

  return (
    <div className="tl-shell">
      <header className="app-header">
        <div className="brand">
          <span className="brand-mark" />
          <span>tekledger</span>
        </div>
        <div className="header-context">
          <span className="header-context-label">Operating workspace</span>
          <div className="crumb">FY{fiscalYear} <span>/</span> {active}</div>
        </div>
        <div className="top-actions">
          <span className="mini">{certificationLabel}</span>
          <button
            type="button"
            className="settings-button"
            data-testid="button-workspace-settings"
            aria-label="Workspace settings"
            title="Workspace settings"
          >
            <Settings size={15} />
          </button>
          <span className="avatar" aria-label="Current user">ER</span>
        </div>
      </header>
      <nav
        ref={workflowNavRef}
        className="workflow-nav"
        aria-label="TekLedger workflow"
      >
        <div className="workflow-track">
          {nav.map(([href, label, Icon], index) => {
            const isActive = index === activeIndex;
            const position =
              activeIndex < 0 || index > activeIndex
                ? "upcoming"
                : index < activeIndex
                  ? "previous"
                  : "current";
            const className = `workflow-step ${isActive ? "active" : ""} ${position}`;
            const testId = `link-${label.toLowerCase().replaceAll(" ", "-")}`;
            const content = (
              <>
                <span className="workflow-step-number">{index + 1}</span>
                <Icon size={15} aria-hidden="true" />
                <span className="workflow-step-label">{label}</span>
              </>
            );
            if (href === "/explain" && onExplain) {
              return (
                <button
                  type="button"
                  onClick={onExplain}
                  key={href}
                  className={className}
                  data-testid={testId}
                  aria-current={isActive ? "step" : undefined}
                  ref={(node) => {
                    if (isActive) activeStepRef.current = node;
                  }}
                >
                  {content}
                </button>
              );
            }
            return (
              <Link
                href={`${href}?${workflowSearch}`}
                key={href}
                className={className}
                data-testid={testId}
                aria-current={isActive ? "step" : undefined}
                ref={(node) => {
                  if (isActive) activeStepRef.current = node;
                }}
              >
                {content}
              </Link>
            );
          })}
        </div>
      </nav>
      <main className="workspace">{children}</main>
    </div>
  );
}

export function formatMoney(value: number | null | undefined, currency = "USD", compact = false) {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    notation: compact ? "compact" : "standard",
    maximumFractionDigits: compact ? 1 : 0,
  }).format(value);
}

export function formatVariance(value: number | null | undefined, currency = "USD") {
  if (value == null) return "—";
  if (value === 0) return formatMoney(0, currency);
  const formatted = formatMoney(Math.abs(value), currency);
  return value > 0 ? `+${formatted} U` : `−${formatted} F`;
}

export function formatPercent(value: number | null | undefined) {
  if (value == null) return "—";
  if (value === 0) return "0%";
  return value > 0 ? `+${value.toFixed(1)}% U` : `−${Math.abs(value).toFixed(1)}% F`;
}

export function formatPeriod(value: string | null | undefined) {
  if (!value) return "—";
  const match = value.match(/^(\d{4})-(\d{2})$/);
  if (!match) return value;
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1)));
}